import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";

import React, { useMemo } from "react";

import {
  Combobox,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { skipToken } from "@tanstack/react-query";
import {
  findByWeight,
  findClosestWeight,
  getAvailableWeights,
  normalizeFontFamily,
} from "@v2/features/modifiers/utils/fontWeightUtils";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { fontRouter } from "@v2/trpc/routes/font";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { FONT_WEIGHT_OPTIONS } from "replo-runtime/shared/utils/font";

interface FontWeightSelectorProps {
  fontWeight: number | null;
  onSelect: (fontWeight: number | null) => void;
  placeholder: string;
  fontFamily: string | null;
}

const FontWeightSelector: React.FC<FontWeightSelectorProps> = ({
  fontWeight,
  onSelect,
  placeholder,
  fontFamily,
}) => {
  const [selectedFontWeight, setSelectedFontWeight] = useOverridableState(
    fontWeight,
    onSelect,
  );

  const projectId = useCurrentProjectId();
  const { data: projectFonts = [] } = fontRouter.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const options = useMemo(() => {
    const defaultWeightOptions = FONT_WEIGHT_OPTIONS.map(
      ({ label, value }) => ({
        label,
        value: value.toString(),
      }),
    );

    if (!fontFamily) {
      return defaultWeightOptions;
    }

    const normalizedFamily = normalizeFontFamily(fontFamily);
    const availableWeights = getAvailableWeights(
      normalizedFamily,
      projectFonts,
    );
    const availableWeightsSet = new Set(
      availableWeights.map((w) => w.toString()),
    );

    const filteredOptions = FONT_WEIGHT_OPTIONS.filter((fw) => {
      return availableWeightsSet.has(fw.value.toString());
    }).map(({ label, value }) => ({
      label,
      value: value.toString(),
    }));

    return filteredOptions.length > 0 ? filteredOptions : defaultWeightOptions;
  }, [fontFamily, projectFonts]);

  const displayOption = useMemo(() => {
    if (selectedFontWeight != null) {
      const exactMatch = findByWeight(options, selectedFontWeight);
      if (exactMatch) {
        return exactMatch;
      }
      const closestWeight = findClosestWeight(
        options.map((opt) => Number(opt.value)),
        selectedFontWeight,
      );
      return findByWeight(options, closestWeight);
    }
    return options.length === 1 ? options[0] : undefined;
  }, [options, selectedFontWeight]);

  const isDisabled = options.length <= 1;

  const items: ComboboxItemWithValue[] = options.map((weight) => ({
    value: weight.value,
    label: weight.label,
  }));

  const selectedItem =
    items.find((item) => item.value === selectedFontWeight?.toString()) ?? null;

  const displayTitle = displayOption?.label ?? placeholder;

  return (
    <div className="w-full overflow-hidden">
      <Combobox
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          if (item) {
            setSelectedFontWeight(Number(item.value));
          }
        }}
        isItemEqualToValue={(a, b) => a.value === b.value}
        trigger={
          <ComboboxTriggerButton
            className="w-full"
            isPlaceholder={!displayOption}
            disabled={isDisabled}
          >
            {displayTitle}
          </ComboboxTriggerButton>
        }
        triggerAsChild
        placeholder="Search..."
        UNSAFE_className="w-[180px]"
      />
    </div>
  );
};

export default FontWeightSelector;
